import { API_ERROR_CODE_META, ApiErrorCodes, ClientErrorCodes, isProofFailureCode } from './errors';
import type { ApiErrorCodeLike } from './errors';
import { CSRF_HEADER, isStateChangingMethod, readCsrfToken } from './csrf';
import { forgetCachedDeviceKey, loadDeviceKey } from './deviceKey';
import { createProof, noteServerTime, PROOF_HEADER } from './proof';
import {
  emitSessionSignal,
  reportServiceHealthy,
  reportServiceUnavailable,
  reportSessionExpiry,
  revocationReasonOf,
  type ProofFailureReason,
} from './sessionSignals';
import { isUserActive, SESSION_PASSIVE_HEADER } from '@/session/activity';
import { reportReachability } from '@/hooks/useOnlineStatus';
import { APP_VERSION_HEADER, noteApiFailure, noteAppVersion } from '@/pwa/versionSkew';
import type { ApiErrorBody, ApiErrorEnvelope } from './types';
import { fromWire, toWire } from './wire';

/**
 * The backend answered. Publishes it to both listeners that care: the session
 * layer's outage latch, and the connection indicator - which is what turns
 * "reconnecting" back into "online" (ADR 0059).
 */
function noteBackendReached(): void {
  reportReachability(true);
  reportServiceHealthy();
}

/** The response header carrying the session's expiry after this request (ADR 0082). */
export const SESSION_EXPIRES_HEADER = 'X-Session-Expires';

/**
 * Hands the server's `X-Session-Expires` to the session layer.
 *
 * Every authenticated response carries it, after whatever slide the request
 * caused, so an active user's expiry moves forward here without a whoami and
 * the "expires soon" banner never shows for them. Read on failures too: a 4xx
 * from a handler still passed the session check.
 */
function noteSessionExpiry(value: string | null | undefined): void {
  if (value) reportSessionExpiry(value);
}

/**
 * The request never left the browser, or nothing answered it.
 *
 * `navigator.onLine` cannot see this - the interface is up, the backend is
 * simply not there - so the indicator is told explicitly, and the app shows
 * "reconnecting" rather than claiming everything is fine.
 */
function unreachable(): ApiError {
  reportReachability(false);
  return new ApiError(0, errorEnvelope(ClientErrorCodes.Network, 'Unable to reach the server.'));
}

/** Base URL of the backend REST surface. */
export const API_BASE: string =
  import.meta.env.VITE_API_BASE ?? '/api/v1';

/** When true the client is wired to the in-memory mock adapter. */
export const USE_MOCKS: boolean = import.meta.env.VITE_USE_MOCKS === 'true';

/**
 * A non-2xx response, parsed out of the backend's error envelope.
 *
 * The wire shape is nested — see Go `httpapi.ErrorEnvelope`:
 * `{"error":{"code":"...","message":"...","details":{},"request_id":"..."}}`
 * The constructor takes that envelope verbatim, so every producer (the real
 * transport and the mock adapter alike) has to speak the real wire format.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCodeLike;
  readonly details: Record<string, unknown> | undefined;
  /** Correlation id from the backend (`error.request_id`), for bug reports. */
  readonly requestId: string | undefined;
  /**
   * Seconds until a rate-limit lockout lifts. Sourced from the `Retry-After`
   * response header when present, else from `details.retry_after_sec`.
   */
  readonly retryAfter: number | undefined;

  constructor(status: number, envelope: ApiErrorEnvelope, retryAfterHeader?: string | null) {
    const body: Partial<ApiErrorBody> = envelope?.error ?? {};
    super(body.message || fallbackErrorMessage(status));
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code || ClientErrorCodes.Unknown;
    this.details = body.details;
    this.requestId = body.request_id;
    this.retryAfter = parseRetryAfter(retryAfterHeader) ?? retryAfterFromDetails(body.details);
  }

  /**
   * Builds an ApiError from a raw response. `payload` is the parsed JSON body
   * (or undefined); anything that is not a well-formed envelope degrades to a
   * synthesised one rather than silently losing the message.
   */
  static fromResponse(status: number, payload: unknown, response?: Response): ApiError {
    return new ApiError(status, toEnvelope(status, payload), response?.headers.get('Retry-After'));
  }

  /** True when `code` is the given backend code — avoids stringly comparisons. */
  is(code: ApiErrorCodeLike): boolean {
    return this.code === code;
  }

  /**
   * The session is gone: expired, revoked, or presented without its key cookie
   * (ADR 0001's `session_invalid`). A `403` is deliberately **not** included —
   * a 403 means "you are signed in and not allowed", which is an authorization
   * error to surface, not a reason to prompt for a password.
   */
  get isSessionExpired(): boolean {
    return this.status === 401;
  }

  /**
   * One of the four `proof_*` 401s (docs/plan/14 §5.4).
   *
   * These are the one class of 401 whose response **leaves the cookie trio in
   * place and keeps the session record** — the request was refused, the
   * session was not. `isSessionExpired` is still true for them (they are
   * 401s), so every caller that branches on "signed out" must check this
   * first; `noteFailure` below does, which is what keeps a missing device key
   * from being published as a sign-out.
   */
  get isProofFailure(): boolean {
    return this.status === 401 && isProofFailureCode(this.code);
  }

  /**
   * `401 session_revoked`: the owner ended this session from elsewhere — a
   * password change, or another session's "Sign out other sessions". Still a
   * 401 (so `isSessionExpired` is true), but a definitive sign-out rather than
   * a re-auth prompt; `noteFailure` dispatches on it before the generic path.
   */
  get isSessionRevoked(): boolean {
    return this.status === 401 && this.code === ApiErrorCodes.SessionRevoked;
  }

  /**
   * The double-submit CSRF token was missing or stale. The one 403 the
   * transport retries, after re-priming the cookie.
   */
  get isCsrfFailure(): boolean {
    return this.status === 403 && this.code === ApiErrorCodes.CsrfInvalid;
  }

  /**
   * The backend is up but its datastore is not (`503 upstream_failure`).
   *
   * This exists precisely so an outage does not present as a signed-out
   * session: the caller must show "temporarily unavailable, retrying" and
   * leave the session alone.
   */
  get isServiceUnavailable(): boolean {
    return this.status === 503 || this.code === ApiErrorCodes.UpstreamFailure;
  }

  /** The request never reached the backend (offline, DNS, CORS). */
  get isOffline(): boolean {
    return this.status === 0;
  }

  /** The backend refused because of rate limiting. */
  get isRateLimited(): boolean {
    return this.status === 429 || this.code === ApiErrorCodes.RateLimited;
  }

  /**
   * `409 idempotency_key_reused` (ADR 0014).
   *
   * The key was presented with a *different* request fingerprint, or a request
   * carrying it is still executing. Either way the operation did not run this
   * time, and re-sending the same key can only produce the same 409 — so it is
   * classified non-retryable in `retry.ts`, and the caller decides whether the
   * logical mutation deserves a fresh key.
   */
  get isIdempotencyConflict(): boolean {
    return this.status === 409 && this.code === ApiErrorCodes.IdempotencyKeyReused;
  }

  /**
   * True when the duplicate is still in flight rather than already finished
   * (`details.in_progress`, with `Retry-After: 1`).
   *
   * The distinction matters to the UI: "your other tab is doing this right
   * now, look again in a second" is not the same as "that key belongs to a
   * different request".
   */
  get idempotencyInProgress(): boolean {
    return this.isIdempotencyConflict && this.details?.in_progress === true;
  }

  /** UIDVALIDITY changed under a cursor: drop the cached list, refetch page 1. */
  get isFolderInvalidated(): boolean {
    return this.status === 409 && this.code === ApiErrorCodes.FolderInvalidated;
  }
}

/** Coerces an arbitrary non-2xx payload into the backend envelope shape. */
/**
 * What a failed response says when its body is not this API's error envelope
 * (#573). The body itself is never shown: during a restart the edge (HAProxy,
 * Caddy) answers with its own HTML page, and a sign-in screen that printed it
 * showed the user a wall of markup. A gateway status means the backend is not
 * there right now; anything else is a plain, generic sentence.
 */
export const SERVER_UNAVAILABLE_MESSAGE =
  "The server is restarting or can't be reached. Try again in a moment.";
export const SERVER_FAILED_MESSAGE = 'Something went wrong on the server. Try again in a moment.';

export function fallbackErrorMessage(status: number): string {
  return status === 502 || status === 503 || status === 504
    ? SERVER_UNAVAILABLE_MESSAGE
    : SERVER_FAILED_MESSAGE;
}

/**
 * True when an error's message is one of the sentences above rather than
 * words the server wrote: a caller with something more specific to say (the
 * send-rate refusal, say) uses its own sentence instead.
 */
export function isFallbackErrorMessage(message: string): boolean {
  return message === SERVER_UNAVAILABLE_MESSAGE || message === SERVER_FAILED_MESSAGE;
}

function toEnvelope(status: number, payload: unknown): ApiErrorEnvelope {
  if (isRecord(payload) && isRecord(payload.error)) {
    const body = payload.error;
    return {
      error: {
        code: typeof body.code === 'string' ? body.code : ClientErrorCodes.Unknown,
        message:
          typeof body.message === 'string' && body.message
            ? body.message
            : fallbackErrorMessage(status),
        ...(isRecord(body.details) ? { details: body.details } : {}),
        ...(typeof body.request_id === 'string' ? { request_id: body.request_id } : {}),
      },
    };
  }
  // Not an envelope: an HTML page from the edge, plain text, an empty body,
  // or some other JSON document. None of it was written for the user, so
  // none of it is shown -- only a sentence chosen by the status.
  return { error: { code: ClientErrorCodes.RequestFailed, message: fallbackErrorMessage(status) } };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `Retry-After` is either delta-seconds or an HTTP-date. */
function parseRetryAfter(header: string | null | undefined): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header.trim());
  if (Number.isFinite(seconds)) return Math.max(0, seconds);
  const when = Date.parse(header);
  if (Number.isNaN(when)) return undefined;
  return Math.max(0, Math.round((when - Date.now()) / 1000));
}

function retryAfterFromDetails(details: Record<string, unknown> | undefined): number | undefined {
  const raw = details?.retry_after_sec;
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.max(0, raw);
  if (typeof raw === 'string') {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return Math.max(0, parsed);
  }
  return undefined;
}

/** Convenience builder for the envelope, so producers never hand-roll it. */
export function errorEnvelope(
  code: ApiErrorCodeLike,
  message: string,
  details?: Record<string, unknown>,
  requestId?: string,
): ApiErrorEnvelope {
  return {
    error: {
      code,
      message,
      ...(details ? { details } : {}),
      ...(requestId ? { request_id: requestId } : {}),
    },
  };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * Serialised as JSON unless it is a FormData or a Blob. A Blob is sent as
   * its raw bytes, `application/octet-stream` (a Nextcloud chunk, ADR 0084).
   */
  body?: unknown;
  query?: Record<string, string | number | boolean | string[] | undefined>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Called on `403 step_up_required` (ADR 0032): prompt for the mailbox
   * password, run the step-up, and resolve `true` to resend or `false` to give
   * up. At most once per logical request, and the resend carries the SAME
   * `Idempotency-Key` -- the backend never records that refusal, so the key
   * still names a request that has not run, and a second key would let a
   * double-submit during the prompt write twice.
   */
  onStepUpRequired?: () => Promise<boolean>;
  /**
   * Pins the `Idempotency-Key` instead of minting a fresh one.
   *
   * The transport's own key is per *call*, which is exactly right for a button
   * press and useless for a mutation that outlives the page that started it.
   * The Outbox (`features/outbox`) mints its key when the message is queued and
   * stores it with the message, so a flush hours later — in a different tab,
   * after a reload — presents the same key and the backend replays its first
   * answer rather than sending the message twice (ADR 0014).
   *
   * The caller owns the rule that comes with it: the same key must carry the
   * same body, because the server fingerprints `sha256(method || path || body)`
   * and answers `409 idempotency_key_reused` otherwise.
   */
  idempotencyKey?: string;
}

export interface UploadOptions extends RequestOptions {
  /**
   * Called with 0..1 as the request body goes out.
   *
   * This is the whole reason `requestUpload` exists: `fetch` reports download
   * progress and nothing at all about the upload, and an attachment is the one
   * request in this app where the wait is long enough that a progress bar is
   * the difference between "working" and "broken".
   */
  onProgress?: (fraction: number) => void;
}

/**
 * Paths whose own 401 is an answer, not a session event.
 *
 * `GET /auth/session` returning 401 *is* the anonymous state; `POST
 * /auth/login` returning 401 is a wrong password. Publishing `expired` for
 * those would open a re-auth modal on the login screen.
 */
const AUTH_PATH_PREFIX = '/auth/';

/**
 * The one path whose `session_revoked` is not news: sign-out itself, which the
 * session layer calls *because* of a revocation. Publishing it again would
 * only ask the session layer to sign out a session it is already signing out.
 */
const LOGOUT_PATH = '/auth/logout';

/**
 * One key per logical call, so a CSRF retry cannot double-send (ADR 0014).
 *
 * The backend honours this on every unsafe method inside `RequireSession`:
 * same key + same fingerprint replays the stored response (with
 * `Idempotency-Replayed: true`), same key + a different fingerprint is
 * `409 idempotency_key_reused`. That is what makes retrying `archive`, `move`,
 * `delete` and `send` safe — on a replay they would otherwise resolve the ids
 * to nothing and report a success as a failure.
 *
 * The key is **also the mutation id**: SSE events caused by the request carry
 * `origin_mutation_id: <key>`, so the originating tab can drop its own echo.
 * `X-Mutation-Id` (doc 03 §7.4) is withdrawn; there is no second header.
 */
function newIdempotencyKey(): string {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (cryptoObj?.randomUUID !== undefined) return cryptoObj.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Thin fetch wrapper. Everything the resource modules do goes through here, so
 * cookie/CSRF plumbing, error normalisation and session signalling live in one
 * place.
 *
 * ## Authentication
 *
 * There is none to attach. The session travels in `HttpOnly` cookies the
 * browser sends by itself (ADR 0001); this class only has to (a) ask for them
 * with `credentials: 'same-origin'` and (b) echo the readable CSRF cookie in a
 * header on state-changing requests.
 *
 * `same-origin`, not `include`: the app is served from the same origin as the
 * API (the dev proxy arranges it), so `include` would only widen the set of
 * requests that ship cookies — and `__Host-` cookies are same-origin by
 * construction anyway.
 */
/**
 * The keepalive body ceiling, with headroom under the browser's 64 KiB limit.
 * The limit is on ALL keepalive requests in flight together, not per request.
 */
const KEEPALIVE_MAX_BODY_BYTES = 48 * 1024;

/**
 * True from the moment the page starts to go away.
 *
 * `visibilityState` alone is not enough. On a RELOAD Chrome fires `pagehide`
 * with the document still `visible`, so a request committed from that handler
 * -- the Undo snackbar's flush, the send undo's commit -- was built without
 * keepalive and aborted as the document was torn down. It had already reached
 * the server, which then had its context cancelled halfway through SMTP: the
 * worst of both, a half-done write with nothing to show for it.
 *
 * `pagehide`, not `beforeunload`: a `beforeunload` the user answers with
 * "stay" fires no counter-event, and the flag would be stuck on for the rest
 * of the session, spending the shared keepalive body budget on every ordinary
 * request. `pageshow` clears it, which is what a bfcache restore fires.
 */
let pageGoingAway = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    pageGoingAway = true;
  });
  window.addEventListener('pageshow', () => {
    pageGoingAway = false;
  });
}

/**
 * Whether a request should outlive the page that issued it.
 *
 * A request started while the page is hidden is, in practice, the Undo
 * snackbar's flush: archive, delete and move are deferred for five seconds, and
 * the pending one is committed on `visibilitychange` → hidden so that closing
 * the tab does not quietly drop it. Without `keepalive` the browser is free to
 * abort that request as the document goes away, and the message the user
 * deleted is back the next time they look. Scoping it to "hidden" rather than
 * turning it on everywhere matters: keepalive requests share one small body
 * budget, so an ordinary busy page must not spend it.
 *
 * Only small JSON bodies qualify. An upload is FormData, can be megabytes, and
 * would simply fail under keepalive — it is better left to be aborted and
 * retried than refused outright.
 */
function shouldKeepAlive(isUpload: boolean, body: RequestInit['body']): boolean {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'hidden' && !pageGoingAway) return false;
  if (isUpload) return false;
  if (body === undefined || body === null) return true;
  return typeof body === 'string' && new TextEncoder().encode(body).length <= KEEPALIVE_MAX_BODY_BYTES;
}

export class HttpTransport {
  constructor(private readonly baseUrl: string = API_BASE) {}

  /**
   * Builds the request URL, converting query-parameter names to `snake_case`.
   *
   * Callers spell parameters in camelCase (`unreadOnly`, `folderId`) like every
   * other key in the codebase; §1.2 requires `unread_only`, `folder_id` on the
   * wire. An array value appends the parameter once per entry, which is how
   * `calendarId: [...]` becomes `calendar_id=…&calendar_id=…`.
   */
  buildUrl(path: string, query?: RequestOptions['query']): string {
    const url = new URL(
      this.baseUrl.replace(/\/$/, '') + path,
      // Absolute VITE_API_BASE wins; a relative one resolves against the origin.
      typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
    );
    const wireQuery = toWire(query ?? {}) as RequestOptions['query'];
    for (const [key, value] of Object.entries(wireQuery ?? {})) {
      if (value === undefined) continue;
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, item);
      } else {
        url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  /**
   * Re-primes `__Host-ols_csrf` by making one plain `GET /auth/session`.
   *
   * Uses `fetch` directly rather than `this.request` so it cannot recurse into
   * another retry, and so its own 401 does not publish a second `expired`
   * signal — the caller's retry will produce the real one.
   */
  private async primeCsrfCookie(signal?: AbortSignal): Promise<void> {
    try {
      const url = this.buildUrl('/auth/session');
      const proof = await this.proofHeader('GET', url);
      await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(proof !== undefined ? { [PROOF_HEADER]: proof } : {}),
        },
        credentials: 'same-origin',
        ...(signal ? { signal } : {}),
      });
    } catch {
      /* The retry will fail on its own and report properly. */
    }
  }

  /**
   * Signs one request, or returns `undefined` when this browser has no key.
   *
   * **`undefined` is never a silent fallback** (ADR 0042). It means the
   * request goes out unbound, and what that is worth is the *server's* answer:
   * an unbound session works, a bound one refuses with `401 proof_required`
   * and the session layer shows the in-place recovery dialog. What the client
   * must never do is decide on its own that a missing key is fine.
   *
   * A signing failure is treated the same way — an unsigned request that the
   * server refuses out loud beats a swallowed exception.
   */
  private async proofHeader(method: string, url: string): Promise<string | undefined> {
    const state = await loadDeviceKey();
    if (state.status !== 'ready') return undefined;
    try {
      return await createProof(state.key, method, url);
    } catch {
      return undefined;
    }
  }

  private async buildInit(
    method: string,
    url: string,
    options: RequestOptions,
    idempotencyKey: string | undefined,
  ): Promise<RequestInit> {
    const { body, signal, headers = {} } = options;
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    const isBytes = !isForm && typeof Blob !== 'undefined' && body instanceof Blob;
    // Read the cookie per request: it rotates on login and password change.
    const csrf = isStateChangingMethod(method) ? readCsrfToken() : null;
    // Signed per request and never reused: `jti` is fresh every time, so a
    // proof captured beside a cookie in an edge log is already spent.
    const proof = await this.proofHeader(method, url);

    const init: RequestInit = {
      method,
      headers: {
        Accept: 'application/json',
        ...(isForm
          ? {}
          : isBytes
            ? { 'Content-Type': 'application/octet-stream' }
            : body !== undefined
              ? { 'Content-Type': 'application/json' }
              : {}),
        // Absent cookie is a normal state — send the request anyway and let the
        // 403 drive one re-prime, rather than guessing or blocking here.
        ...(csrf !== null ? { [CSRF_HEADER]: csrf } : {}),
        ...(proof !== undefined ? { [PROOF_HEADER]: proof } : {}),
        ...(idempotencyKey !== undefined ? { 'Idempotency-Key': idempotencyKey } : {}),
        // Nobody at the keyboard (or the tab is hidden): this is a poll or a
        // background refetch, and it must not slide the session (ADR 0082).
        // Decided per attempt, at send time, which is when it is true.
        ...(isUserActive() ? {} : { [SESSION_PASSIVE_HEADER]: '1' }),
        ...headers,
      },
      credentials: 'same-origin',
    };
    // JSON bodies are camelCase everywhere above the transport and `snake_case`
    // on the wire (ADR 0009). `FormData` is handed over untouched: its field
    // names are the caller's, and walking it would corrupt the upload. A Blob
    // is the body itself, byte for byte.
    if (body !== undefined) {
      init.body = isForm || isBytes ? (body as FormData | Blob) : JSON.stringify(toWire(body));
    }
    if (signal) init.signal = signal;
    if (shouldKeepAlive(isForm || isBytes, init.body)) init.keepalive = true;
    return init;
  }

  /**
   * Classifies a failure once, for every caller.
   *
   * The 401/503 split is the important part: both mean "your request did not
   * happen", and only one of them means "you are signed out".
   */
  private noteFailure(path: string, error: ApiError): void {
    // Version skew (ADR 0103): a route or field this backend no longer has,
    // after a deploy this page has seen, means the page must reload.
    noteApiFailure(error);
    if (error.isServiceUnavailable) {
      reportServiceUnavailable(error.retryAfter);
      return;
    }
    // Dispatched on the code **before** the generic sign-out path (doc 10
    // §1.8 as amended). The backend left the cookies and the record alone, so
    // this is "your device key is missing or wrong", not "you are signed out",
    // and the two have completely different costs to the user.
    if (error.isProofFailure) {
      emitSessionSignal({
        type: 'proof',
        reason: proofReasonOf(error.code),
        code: String(error.code),
      });
      return;
    }
    // Also ahead of the generic path, and — unlike it — published for `/auth/`
    // paths too: whoami answering `session_revoked` is not the ordinary
    // anonymous state but the reason the sign-in screen has to give.
    if (error.isSessionRevoked) {
      if (path !== LOGOUT_PATH) {
        emitSessionSignal({ type: 'revoked', reason: revocationReasonOf(error.details) });
      }
      return;
    }
    // `invalid_credentials` is a password the user just TYPED being wrong --
    // a step-up such as `POST /accounts/{id}/password` -- and the session
    // that sent it is untouched (the server clears no cookie for it). Treating
    // it as a sign-out opened the re-auth modal over the form whose answer it
    // was, so a mistyped current password looked like being logged out.
    if (
      error.isSessionExpired &&
      !path.startsWith(AUTH_PATH_PREFIX) &&
      !error.is(ApiErrorCodes.InvalidCredentials)
    ) {
      emitSessionSignal({ type: 'expired', code: String(error.code), status: error.status });
    }
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return (await this.requestWithStatus<T>(path, options)).body;
  }

  /**
   * `request`, answering the 2xx status beside the body.
   *
   * For the one route whose success statuses mean different things: the
   * Nextcloud link poll answers 202 while the person has not granted access
   * yet and 200 once they have (ADR 0084). Everything else wants `request`.
   */
  async requestWithStatus<T>(
    path: string,
    options: RequestOptions = {},
  ): Promise<{ status: number; body: T }> {
    const method = options.method ?? 'GET';
    // One key per logical request, reused by the retry below: the point of the
    // key is that the server can recognise the replay, so regenerating it on
    // retry would defeat it entirely.
    const idempotencyKey = isStateChangingMethod(method)
      ? (options.idempotencyKey ?? newIdempotencyKey())
      : undefined;

    // Each recovery is worth exactly one attempt, tracked per request: a
    // rotated cookie, a jumped clock, a key another tab replaced and a request
    // the network delivered twice all fix themselves once, and a loop would
    // hammer a backend that has said no.
    const retries: RetryBudget = {
      csrf: true,
      stale: true,
      keyReload: true,
      replay: true,
      stepUp: true,
    };

    for (;;) {
      const url = this.buildUrl(path, options.query);
      const init = await this.buildInit(method, url, options, idempotencyKey);

      let response: Response;
      try {
        response = await fetch(url, init);
      } catch (cause) {
        if (options.signal?.aborted) throw cause;
        throw unreachable();
      }
      noteSessionExpiry(response.headers.get(SESSION_EXPIRES_HEADER));
      noteAppVersion(response.headers.get(APP_VERSION_HEADER), method, path);

      if (response.ok) {
        noteBackendReached();
        // Read even when there is nothing to read. A 204 whose body is never
        // consumed is logged by Chromium as `(canceled) net::ERR_ABORTED`,
        // right after its own 204: every DELETE and the logout looked like a
        // request sent twice with the second one aborted (Gitea #343). The
        // request itself was fine; the network log was not.
        const text = await response.text();
        if (response.status === 204) return { status: 204, body: undefined as T };
        // 2xx only. The error path below never calls `fromWire`, which is what
        // keeps the error envelope out of the conversion (ADR 0009): `ApiError`
        // reads `request_id` and raw `details` keys verbatim.
        const parsed = (text ? fromWire<T>(safeParse(text)) : undefined) as T;
        // `server_time` rides along on the `Session` body of whoami, login and
        // refresh. Picking it up here rather than in three call sites is what
        // keeps `iat` on the server's clock instead of the device's.
        noteServerTimeFrom(parsed);
        return { status: response.status, body: parsed };
      }

      const text = await response.text();
      const error = ApiError.fromResponse(
        response.status,
        text ? safeParse(text) : undefined,
        response,
      );

      if (await this.recover(error, retries, options.signal, options.onStepUpRequired)) continue;

      this.noteFailure(path, error);
      throw error;
    }
  }

  /**
   * Decides whether one failure is worth exactly one more attempt.
   *
   * Extracted so `requestUpload` gets the same three recoveries `request` has
   * rather than a second, thinner set of them: a rotated CSRF cookie or a
   * device key another tab replaced must not fail an attachment upload when it
   * would not fail anything else.
   */
  private async recover(
    error: ApiError,
    retries: RetryBudget,
    signal: AbortSignal | undefined,
    onStepUpRequired?: () => Promise<boolean>,
  ): Promise<boolean> {
    // Exactly one retry, tracked on the request, after re-priming the cookie.
    // A rotated cookie is the common cause and it fixes itself; a loop here
    // would hammer a backend that has genuinely rejected us.
    if (error.isCsrfFailure && retries.csrf) {
      retries.csrf = false;
      await this.primeCsrfCookie(signal);
      return true;
    }

    // The clock jumped (or was always wrong). Re-sync from the server's own
    // timestamp and sign again — same `Idempotency-Key`, fresh `jti` and
    // `iat`, so the replay still resolves correctly (ADR 0043).
    if (
      error.is(ApiErrorCodes.ProofStale) &&
      retries.stale &&
      noteServerTime(readServerTime(error.details))
    ) {
      retries.stale = false;
      return true;
    }

    // Usually another tab rebound the session under us. Drop the cached
    // handle, re-read IndexedDB, try once. Only then is it a real failure.
    if (error.is(ApiErrorCodes.ProofInvalid) && retries.keyReload) {
      retries.keyReload = false;
      forgetCachedDeviceKey();
      return true;
    }

    // This request's `jti` reached the server before. Not because we re-sent
    // it -- every attempt is signed afresh in `buildInit` -- but because
    // something between us and the handler did: a proxy's HTTP client or the
    // browser's own network stack re-sends an idempotent request when the
    // connection drops before the response (a deploy replacing the backend, a
    // laptop waking, a phone changing network). The server is right to refuse
    // the copy; the key and the session are fine. Sign once more and retry,
    // rather than asking a user with a healthy session for their password.
    // A thief replaying a captured header cannot do this: it has no key.
    //
    // `425 too_early` shares the budget: the edge received this request in
    // TLS early data and the backend refused it without spending the proof.
    // Browsers retry that on their own after the handshake (RFC 8470); this
    // covers a client that hands the 425 back to us instead.
    if (
      (error.is(ApiErrorCodes.ProofReplayed) || error.is(ApiErrorCodes.TooEarly)) &&
      retries.replay
    ) {
      retries.replay = false;
      return true;
    }

    // An admin write without a live elevation (ADR 0032). The caller's handler
    // asks for the password; only a step-up that succeeded earns the resend,
    // and only one -- a second refusal means something the prompt cannot fix.
    if (error.is(ApiErrorCodes.StepUpRequired) && retries.stepUp && onStepUpRequired) {
      retries.stepUp = false;
      return onStepUpRequired();
    }
    return false;
  }

  /**
   * `POST`s a `FormData` body with upload progress, over `XMLHttpRequest`.
   *
   * Everything that makes a request legitimate — the CSRF header, the device
   * proof, the idempotency key, the cookies — comes from `buildInit`, the same
   * function `request` uses, so there is exactly one place that decides how a
   * request is authenticated. Session binding is `required` on this
   * deployment: a hand-rolled `fetch` or `XMLHttpRequest` that skipped it
   * would be refused with `401 proof_required`, and this method exists so no
   * caller is ever tempted to write one.
   *
   * XHR rather than `fetch` for one reason: `upload.onprogress`. A `fetch`
   * request stream would work in Chrome and nowhere else, and needs HTTP/2.
   */
  async requestUpload<T>(path: string, options: UploadOptions = {}): Promise<T> {
    const method = options.method ?? 'POST';
    const idempotencyKey = isStateChangingMethod(method)
      ? (options.idempotencyKey ?? newIdempotencyKey())
      : undefined;
    const retries: RetryBudget = {
      csrf: true,
      stale: true,
      keyReload: true,
      replay: true,
      stepUp: true,
    };

    for (;;) {
      const url = this.buildUrl(path, options.query);
      const init = await this.buildInit(method, url, options, idempotencyKey);

      let result: XhrResult;
      try {
        result = await sendXhr(method, url, init, options);
      } catch (cause) {
        if (options.signal?.aborted) throw cause;
        throw unreachable();
      }

      noteSessionExpiry(result.sessionExpires);
      noteAppVersion(result.appVersion, method, path);
      if (result.status >= 200 && result.status < 300) {
        noteBackendReached();
        if (result.status === 204 || result.text === '') return undefined as T;
        return fromWire<T>(safeParse(result.text));
      }

      const error = new ApiError(
        result.status,
        toEnvelope(result.status, result.text ? safeParse(result.text) : undefined),
        result.retryAfter,
      );
      if (await this.recover(error, retries, options.signal, options.onStepUpRequired)) continue;

      this.noteFailure(path, error);
      throw error;
    }
  }

  /** For endpoints that return a file rather than JSON (ICS export, raw source). */
  async requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const method = options.method ?? 'GET';
    const idempotencyKey = isStateChangingMethod(method)
      ? (options.idempotencyKey ?? newIdempotencyKey())
      : undefined;
    const url = this.buildUrl(path, options.query);
    // Proof-bound like everything else. This is the path attachments, `source`,
    // `export.ics` and contact photos take now that no authenticated GET may be
    // a plain browser navigation (ADR 0041) — a binding that covered `fetch`
    // only would leave attachments readable with a stolen cookie.
    const init = await this.buildInit(method, url, options, idempotencyKey);
    // Accept: application/json is wrong for a binary endpoint.
    delete (init.headers as Record<string, string>).Accept;

    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (cause) {
      if (options.signal?.aborted) throw cause;
      throw unreachable();
    }
    noteSessionExpiry(response.headers.get(SESSION_EXPIRES_HEADER));
    noteAppVersion(response.headers.get(APP_VERSION_HEADER), method, path);
    if (!response.ok) {
      const error = ApiError.fromResponse(
        response.status,
        await safeErrorPayload(response),
        response,
      );
      this.noteFailure(path, error);
      throw error;
    }
    noteBackendReached();
    return response.blob();
  }

  /** For endpoints that return text/plain (RFC822 source). */
  async requestText(path: string, options: RequestOptions = {}): Promise<string> {
    const blob = await this.requestBlob(path, options);
    return blob.text();
  }

  /**
   * `POST`s JSON and reads a `text/event-stream` answer as it arrives — the
   * AI assistant's streamed reply (ADR 0065).
   *
   * Authenticated exactly like `request` (the same `buildInit`: CSRF header,
   * device proof, cookies) and recovered the same way while the server is
   * still refusing with an ordinary JSON error: one CSRF re-prime, one clock
   * re-sync, one key reload. Once the stream has started there is nothing to
   * retry, and nothing is: an `error` event rejects with an `ApiError` built
   * from its `{code, message, details}`, and every other event is handed to
   * `onEvent` with its data already converted from the wire's snake_case.
   *
   * No `Idempotency-Key`: the route sits outside the idempotency layer, which
   * buffers whole responses, and an assist changes nothing a replay could
   * double.
   */
  async requestEventStream(
    path: string,
    body: unknown,
    onEvent: (event: string, data: unknown, raw: unknown) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const retries: RetryBudget = { csrf: true, stale: true, keyReload: true, replay: true, stepUp: false };
    const options: RequestOptions = { method: 'POST', body, ...(signal ? { signal } : {}) };
    for (;;) {
      const url = this.buildUrl(path);
      const init = await this.buildInit('POST', url, options, undefined);
      (init.headers as Record<string, string>).Accept = 'text/event-stream';
      // A stream is never `keepalive`: that caps the body and outlives the page.
      delete init.keepalive;

      let response: Response;
      try {
        response = await fetch(url, init);
      } catch (cause) {
        if (signal?.aborted) throw cause;
        throw unreachable();
      }
      noteSessionExpiry(response.headers.get(SESSION_EXPIRES_HEADER));
      noteAppVersion(response.headers.get(APP_VERSION_HEADER), 'POST', path);
      if (!response.ok) {
        const error = ApiError.fromResponse(response.status, await safeErrorPayload(response), response);
        if (await this.recover(error, retries, signal)) continue;
        this.noteFailure(path, error);
        throw error;
      }
      noteBackendReached();
      if (response.body === null) return;
      await readEventStream(response.body, (event, raw) => {
        const data = safeParse(raw);
        if (event === 'error') {
          const envelope = { error: (data ?? {}) as ApiErrorBody };
          const code = envelope.error.code;
          const status =
            code !== undefined && code in API_ERROR_CODE_META
              ? API_ERROR_CODE_META[code as keyof typeof API_ERROR_CODE_META].status
              : 502;
          throw new ApiError(status, envelope);
        }
        // `raw` is the event as the server wrote it (snake_case), for a
        // caller that rebuilds an ApiError from it: error details are read
        // by their wire names everywhere else.
        onEvent(event, fromWire(data), data);
      });
      return;
    }
  }
}

/**
 * Reads a `text/event-stream` body, calling `onEvent` once per complete event
 * (a blank line ends one). Only `event:` and `data:` are understood; `id:`,
 * `retry:` and comments are skipped, since this is one POST's answer and not
 * a reconnecting EventSource. A throw from `onEvent` cancels the body.
 */
export async function readEventStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let event = 'message';
  let data: string[] = [];
  const dispatch = (): void => {
    if (data.length > 0) onEvent(event, data.join('\n'));
    event = 'message';
    data = [];
  };
  const line = (text: string): void => {
    if (text === '') {
      dispatch();
    } else if (text.startsWith('event:')) {
      event = text.slice(6).trim();
    } else if (text.startsWith('data:')) {
      data.push(text.slice(5).replace(/^ /, ''));
    }
  };
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let at: number;
      while ((at = buffer.search(/\r\n|\n|\r/)) >= 0) {
        const newline = buffer.startsWith('\r\n', at) ? 2 : 1;
        line(buffer.slice(0, at));
        buffer = buffer.slice(at + newline);
      }
    }
    buffer += decoder.decode();
    if (buffer !== '') line(buffer);
    dispatch();
  } catch (error) {
    await reader.cancel().catch(() => undefined);
    throw error;
  } finally {
    reader.releaseLock();
  }
}

/** One attempt of each recovery, tracked per logical request. */
interface RetryBudget {
  csrf: boolean;
  stale: boolean;
  keyReload: boolean;
  replay: boolean;
  stepUp: boolean;
}

interface XhrResult {
  status: number;
  text: string;
  retryAfter: string | null;
  sessionExpires: string | null;
  appVersion: string | null;
}

/**
 * One `XMLHttpRequest`, resolved with the raw response rather than thrown on.
 *
 * `withCredentials` is left alone deliberately: XHR sends same-origin cookies
 * by default, and setting it would widen the request to cross-origin
 * credentials, which is exactly what `credentials: 'same-origin'` avoids on
 * the fetch path.
 */
function sendXhr(
  method: string,
  url: string,
  init: RequestInit,
  options: UploadOptions,
): Promise<XhrResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
    for (const [name, value] of Object.entries(init.headers as Record<string, string>)) {
      // The browser owns `Content-Type` for a FormData body: it has to append
      // the multipart boundary, and a header we set would replace it with one
      // that has no boundary at all. Raw bytes keep ours: a sliced Blob has no
      // type, and the browser would otherwise send none.
      if (isForm && name.toLowerCase() === 'content-type') continue;
      xhr.setRequestHeader(name, value);
    }

    const onAbort = () => xhr.abort();
    options.signal?.addEventListener('abort', onAbort);
    const done = () => options.signal?.removeEventListener('abort', onAbort);

    if (options.onProgress) {
      const report = options.onProgress;
      xhr.upload.onprogress = (event) => {
        // `lengthComputable` is false for a chunked body; reporting a made-up
        // fraction would be worse than reporting none.
        if (event.lengthComputable && event.total > 0) report(event.loaded / event.total);
      };
    }
    xhr.onload = () => {
      done();
      // The bytes are all on the wire by the time the response arrives, so the
      // bar reaches the end even when the browser gave no final progress event.
      options.onProgress?.(1);
      resolve({
        status: xhr.status,
        text: xhr.responseText ?? '',
        retryAfter: xhr.getResponseHeader('Retry-After'),
        sessionExpires: xhr.getResponseHeader(SESSION_EXPIRES_HEADER),
        appVersion: xhr.getResponseHeader(APP_VERSION_HEADER),
      });
    };
    xhr.onerror = () => {
      done();
      reject(new Error('network error'));
    };
    xhr.onabort = () => {
      done();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    xhr.send((init.body as XMLHttpRequestBodyInit | undefined) ?? null);
  });
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

/** Best-effort read of an error body on the blob/text paths. */
async function safeErrorPayload(response: Response): Promise<unknown> {
  try {
    const text = await response.clone().text();
    return text ? safeParse(text) : { message: response.statusText };
  } catch {
    return { message: response.statusText };
  }
}

/**
 * Maps a `proof_*` code to the reason the recovery UI reads.
 *
 * Anything unrecognised is reported as `required`, which is the branch whose
 * copy explains a missing key — the honest default, because the only way to
 * get here with an unknown code is a backend ahead of this build.
 */
function proofReasonOf(code: ApiErrorCodeLike): ProofFailureReason {
  switch (code) {
    case ApiErrorCodes.ProofInvalid:
      return 'invalid';
    case ApiErrorCodes.ProofStale:
      return 'stale';
    case ApiErrorCodes.ProofReplayed:
      return 'replayed';
    default:
      return 'required';
  }
}

/** `details.server_time` off a `401 proof_stale`. Raw key: it is an envelope. */
function readServerTime(details: Record<string, unknown> | undefined): string | undefined {
  const raw = details?.server_time;
  return typeof raw === 'string' ? raw : undefined;
}

/** Picks `serverTime` out of any 2xx body that carries one. */
function noteServerTimeFrom(payload: unknown): void {
  if (!isRecord(payload)) return;
  const value = payload.serverTime;
  if (typeof value === 'string') noteServerTime(value);
}
